import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import api from '../utils/api';
import { Calendar, TrendingUp, TrendingDown, BarChart3, LineChart as LineChartIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BookingAnalytics = () => {
  const [period, setPeriod] = useState('month'); // day, week, month, quarter, year
  const [currentData, setCurrentData] = useState([]);
  const [previousData, setPreviousData] = useState([]);
  const [chartType, setChartType] = useState('bar'); // bar or line
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    current: { count: 0, revenue: 0 },
    previous: { count: 0, revenue: 0 },
    twoYearsAgo: { count: 0, revenue: 0 }
  });

  useEffect(() => {
    fetchAnalyticsData();
  }, [period]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      // Get all bookings for the last 3 years
      const currentYear = new Date().getFullYear();
      const promises = [];
      
      for (let i = 0; i < 3; i++) {
        promises.push(api.get(`/bookings?year=${currentYear - i}`));
      }
      
      const responses = await Promise.all(promises);
      const allBookings = responses.flatMap(r => r.data);
      
      // Process data based on selected period
      processDataByPeriod(allBookings);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const processDataByPeriod = (bookings) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    let currentPeriodData = [];
    let previousYearData = [];
    let twoYearsAgoData = [];
    
    switch (period) {
      case 'day':
        currentPeriodData = filterByDay(bookings, now);
        previousYearData = filterByDay(bookings, new Date(currentYear - 1, now.getMonth(), now.getDate()));
        twoYearsAgoData = filterByDay(bookings, new Date(currentYear - 2, now.getMonth(), now.getDate()));
        break;
        
      case 'week':
        currentPeriodData = filterByWeek(bookings, now);
        previousYearData = filterByWeek(bookings, new Date(currentYear - 1, now.getMonth(), now.getDate()));
        twoYearsAgoData = filterByWeek(bookings, new Date(currentYear - 2, now.getMonth(), now.getDate()));
        break;
        
      case 'month':
        currentPeriodData = filterByMonth(bookings, now);
        previousYearData = filterByMonth(bookings, new Date(currentYear - 1, now.getMonth(), 1));
        twoYearsAgoData = filterByMonth(bookings, new Date(currentYear - 2, now.getMonth(), 1));
        break;
        
      case 'quarter':
        currentPeriodData = filterByQuarter(bookings, now);
        previousYearData = filterByQuarter(bookings, new Date(currentYear - 1, now.getMonth(), 1));
        twoYearsAgoData = filterByQuarter(bookings, new Date(currentYear - 2, now.getMonth(), 1));
        break;
        
      case 'year':
        currentPeriodData = filterByYear(bookings, currentYear);
        previousYearData = filterByYear(bookings, currentYear - 1);
        twoYearsAgoData = filterByYear(bookings, currentYear - 2);
        break;
    }
    
    // Calculate statistics
    const currentStats = calculateStats(currentPeriodData);
    const previousStats = calculateStats(previousYearData);
    const twoYearsStats = calculateStats(twoYearsAgoData);
    
    setStats({
      current: currentStats,
      previous: previousStats,
      twoYearsAgo: twoYearsStats
    });
    
    // Prepare chart data
    prepareChartData(currentPeriodData, previousYearData, twoYearsAgoData);
  };

  const filterByDay = (bookings, date) => {
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);
    
    return bookings.filter(b => {
      const created = new Date(b.created_at);
      return created >= dayStart && created <= dayEnd && !b.cancelled;
    });
  };

  const filterByWeek = (bookings, date) => {
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay());
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    
    return bookings.filter(b => {
      const created = new Date(b.created_at);
      return created >= weekStart && created <= weekEnd && !b.cancelled;
    });
  };

  const filterByMonth = (bookings, date) => {
    return bookings.filter(b => {
      const created = new Date(b.created_at);
      return created.getFullYear() === date.getFullYear() &&
             created.getMonth() === date.getMonth() &&
             !b.cancelled;
    });
  };

  const filterByQuarter = (bookings, date) => {
    const quarter = Math.floor(date.getMonth() / 3);
    const quarterStart = quarter * 3;
    
    return bookings.filter(b => {
      const created = new Date(b.created_at);
      const bookingQuarter = Math.floor(created.getMonth() / 3);
      return created.getFullYear() === date.getFullYear() &&
             bookingQuarter === quarter &&
             !b.cancelled;
    });
  };

  const filterByYear = (bookings, year) => {
    return bookings.filter(b => {
      const created = new Date(b.created_at);
      return created.getFullYear() === year && !b.cancelled;
    });
  };

  const calculateStats = (bookings) => {
    return {
      count: bookings.length,
      revenue: bookings.reduce((sum, b) => sum + (b.guest_total || 0), 0)
    };
  };

  const prepareChartData = (current, previous, twoYears) => {
    const currentYear = new Date().getFullYear();
    
    const data = [
      {
        name: `${currentYear - 2}`,
        prenotazioni: twoYears.length,
        incassi: twoYears.reduce((sum, b) => sum + (b.guest_total || 0), 0)
      },
      {
        name: `${currentYear - 1}`,
        prenotazioni: previous.length,
        incassi: previous.reduce((sum, b) => sum + (b.guest_total || 0), 0)
      },
      {
        name: `${currentYear}`,
        prenotazioni: current.length,
        incassi: current.reduce((sum, b) => sum + (b.guest_total || 0), 0)
      }
    ];
    
    setCurrentData(data);
  };

  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const getPeriodLabel = () => {
    const labels = {
      day: 'Giorno',
      week: 'Settimana',
      month: 'Mese',
      quarter: 'Trimestre',
      year: 'Anno'
    };
    return labels[period];
  };

  const getDateRange = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    switch (period) {
      case 'day':
        return now.toLocaleDateString('it-IT');
      case 'week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return `${weekStart.toLocaleDateString('it-IT')} - ${weekEnd.toLocaleDateString('it-IT')}`;
      case 'month':
        return now.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3) + 1;
        return `Q${quarter} ${currentYear}`;
      case 'year':
        return currentYear.toString();
      default:
        return '';
    }
  };

  const countChange = calculatePercentageChange(stats.current.count, stats.previous.count);
  const revenueChange = calculatePercentageChange(stats.current.revenue, stats.previous.revenue);

  if (loading) {
    return <Layout title="Analytics Prenotazioni"><div className="p-8">Caricamento...</div></Layout>;
  }

  return (
    <Layout title="Analytics Prenotazioni">
      <div className="space-y-6">
        {/* Header Controls */}
        <Card className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold">Analytics Prenotazioni</h2>
              <p className="text-sm text-slate-600 mt-1">
                Analisi comparativa con anni precedenti
              </p>
            </div>
            
            <div className="flex gap-3">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Giorno</SelectItem>
                  <SelectItem value="week">Settimana</SelectItem>
                  <SelectItem value="month">Mese</SelectItem>
                  <SelectItem value="quarter">Trimestre</SelectItem>
                  <SelectItem value="year">Anno</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={chartType} onValueChange={setChartType}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Barre</SelectItem>
                  <SelectItem value="line">Linee</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Period Info */}
        <Card className="p-6 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            <span className="font-semibold text-blue-900">
              Periodo Analizzato: {getPeriodLabel()} - {getDateRange()}
            </span>
          </div>
        </Card>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Prenotazioni */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Numero Prenotazioni
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-600">Anno Corrente</p>
                  <p className="text-3xl font-bold text-green-700">{stats.current.count}</p>
                </div>
                {countChange !== 0 && (
                  <Badge className={countChange > 0 ? 'bg-green-500' : 'bg-red-500'}>
                    {countChange > 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                    {countChange > 0 ? '+' : ''}{countChange.toFixed(1)}%
                  </Badge>
                )}
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-600">Anno Precedente</p>
                  <p className="text-2xl font-semibold text-slate-700">{stats.previous.count}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-600">2 Anni Fa</p>
                  <p className="text-2xl font-semibold text-slate-700">{stats.twoYearsAgo.count}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Incassi */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Totale Incassi
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-600">Anno Corrente</p>
                  <p className="text-3xl font-bold text-blue-700">€{stats.current.revenue.toFixed(2)}</p>
                </div>
                {revenueChange !== 0 && (
                  <Badge className={revenueChange > 0 ? 'bg-green-500' : 'bg-red-500'}>
                    {revenueChange > 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                    {revenueChange > 0 ? '+' : ''}{revenueChange.toFixed(1)}%
                  </Badge>
                )}
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-600">Anno Precedente</p>
                  <p className="text-2xl font-semibold text-slate-700">€{stats.previous.revenue.toFixed(2)}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-600">2 Anni Fa</p>
                  <p className="text-2xl font-semibold text-slate-700">€{stats.twoYearsAgo.revenue.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Prenotazioni Chart */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Confronto Prenotazioni</h3>
            <ResponsiveContainer width="100%" height={300}>
              {chartType === 'bar' ? (
                <BarChart data={currentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="prenotazioni" fill="#10b981" />
                </BarChart>
              ) : (
                <LineChart data={currentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="prenotazioni" stroke="#10b981" strokeWidth={3} />
                </LineChart>
              )}
            </ResponsiveContainer>
          </Card>

          {/* Incassi Chart */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Confronto Incassi</h3>
            <ResponsiveContainer width="100%" height={300}>
              {chartType === 'bar' ? (
                <BarChart data={currentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => `€${value.toFixed(2)}`} />
                  <Legend />
                  <Bar dataKey="incassi" fill="#3b82f6" />
                </BarChart>
              ) : (
                <LineChart data={currentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => `€${value.toFixed(2)}`} />
                  <Legend />
                  <Line type="monotone" dataKey="incassi" stroke="#3b82f6" strokeWidth={3} />
                </LineChart>
              )}
            </ResponsiveContainer>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default BookingAnalytics;
